import { ChangeEvent, FormEvent, useState } from "react";
import axios, { AxiosError } from "axios";

import { useSelector } from "redux/hooks";
import { getFullUser } from "redux/selectors/user";

import Input from "components/botanic/Input";

export default function PasswordSettings({ className }: { className: string }) {
  const user = useSelector(getFullUser);

  const [passwordState, setPasswordState] = useState({
    confirmNew: "",
    invalidCurrentPassword: false,
    invalidNewPassword: false,
    new: "",
    old: "",
    passwordChangeFail: false,
    passwordChangeSuccess: false,
    passwordsDontMatch: false,
  });

  const updatePassword = async (e: FormEvent) => {
    e.preventDefault();

    const result = {
      invalidCurrentPassword: false,
      invalidNewPassword: false,
      passwordChangeFail: false,
      passwordChangeSuccess: false,
      passwordsDontMatch: passwordState.new !== passwordState.confirmNew,
    };

    if (result.passwordsDontMatch) {
      setPasswordState({
        ...passwordState,
        ...result,
      });

      return;
    }

    try {
      const response = await axios.post("/api/profile/update-password", {
        newPassword: passwordState.new,
        oldPassword: passwordState.old,
        user,
      });

      if (response?.status === 200) {
        result.passwordChangeSuccess = true;
      } else {
        result.passwordChangeFail = true;
      }
    } catch (e) {
      const message = (e as AxiosError<{ body?: { reason: string } }>)?.response
        ?.data?.body?.reason;
      if (message) {
        switch (message) {
          case "INVALID_OLD_PASSWORD":
            result.invalidCurrentPassword = true;
            break;
          case "INVALID_NEW_PASSWORD":
            result.invalidNewPassword = true;
            break;
          default:
            result.passwordChangeFail = true;
            break;
        }
      }
    }

    setPasswordState({
      ...passwordState,
      ...result,
    });
  };

  const handleOldPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setPasswordState({
      ...passwordState,
      old: event.target.value,
      passwordChangeSuccess: false,
    });
  };

  const handleNewPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setPasswordState({
      ...passwordState,
      new: event.target.value,
      passwordChangeSuccess: false,
    });
  };

  const handleConfirmNewPassword = (event: ChangeEvent<HTMLInputElement>) => {
    setPasswordState({
      ...passwordState,
      confirmNew: event.target.value,
      passwordChangeSuccess: false,
    });
  };

  return (
    <div className={className}>
      <h3 className="heading--m mb-lg">Change password</h3>
      {passwordState.passwordChangeSuccess && (
        <div className="row">
          <div className="bg-green-20 border-2 border-green text-center mb-xl shadow-low rounded w-full p-md">
            Your password was successfully updated.
          </div>
        </div>
      )}
      {passwordState.passwordChangeFail && (
        <div className="row items-center">
          <div className="col md:col-3/4">
            <div className="text-xs text-error pt-xs">
              There was an error changing your password. Please refresh the page
              and try again."
            </div>
          </div>
        </div>
      )}
      {/* Checking for user.username here so we can render the form with the
      username value present to avoid react errors when changing the input value.
      Google suggests including the username input for accessibility.
      https://www.chromium.org/developers/design-documents/create-amazing-password-forms  */}
      {user.username && (
        <form onSubmit={updatePassword}>
          <div className="hidden">
            <Input
              id="username"
              className="fs-exclude"
              disabled={true}
              type="hidden"
              autoComplete="username"
              name="username"
              value={user.username}
            />
          </div>

          <div className="mb-md">
            <Input
              id="oldPassword"
              label="Old password"
              className="fs-exclude"
              type="password"
              onChange={handleOldPassword}
              autoComplete="current-password"
              errorMessage={
                passwordState.invalidCurrentPassword
                  ? "Wrong password. Please try again"
                  : ""
              }
            />
          </div>
          <div className="mb-md">
            <Input
              id="newPassword"
              label="New password"
              className="fs-exclude"
              autoComplete="new-password"
              type="password"
              onChange={handleNewPassword}
              errorMessage={
                passwordState.invalidNewPassword
                  ? "Password must be at least 8 characters long and may not contain the same character repeated more than 3 times in a row."
                  : ""
              }
            />
          </div>
          <div className="mb-lg">
            <Input
              id="confirmNewPassword"
              label="Confirm new password"
              autoComplete="new-password"
              className="fs-exclude"
              type="password"
              onChange={handleConfirmNewPassword}
              errorMessage={
                passwordState.passwordsDontMatch
                  ? "New passwords don't match."
                  : ""
              }
            />
          </div>

          <div className="row">
            <div className="col md:col-10">
              <button className="button" tabIndex={0} type="submit">
                update password
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
